import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { errorToastClassName, setFullscreenToastQueueing, appToast as toast } from "@/components/ui/baseUi/sonner"
import { word } from "@modules/core/utils/i18n"
import { useEffect, useImperativeHandle, useRef, useState } from "react"

export type FullscreenModalRef = {
  open: () => void
}
type FullscreenModalProps = {
  children: React.ReactNode
  ref: React.Ref<FullscreenModalRef>
}

export function FullscreenModal({ children, ref }: FullscreenModalProps) {
  const [showModal, setShowModal] = useState(false)
  const [showIcons, setShowIcons] = useState(false)
  const { setTheme, theme } = useTheme()
  const [isThemeSwap, setIsThemeSwap] = useState(false)

  const iconTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const handleMouseMove = () => {
    setShowIcons(true)
    // hide icons after 2 sec of inactivity
    clearTimeout(iconTimeout.current)
    iconTimeout.current = setTimeout(() => {
      setShowIcons(false)
    }, 2000)
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      if (document.documentElement.requestFullscreen) {
        document.documentElement
          .requestFullscreen()
          .catch((err) => {
            if (err instanceof Error) {
              toast.error(word("program.session.displayFullScreenNotSupported.title"), {
                description: err.message,
                classNames: errorToastClassName(),
              })
            }
          })
          .then(() => {
            window.addEventListener("mousemove", handleMouseMove)
            // swap to ligh theme if needed
            if (theme === Theme.OwnerDark) {
              setTheme(Theme.AthleteLight)
              setIsThemeSwap(true)
            }

            setFullscreenToastQueueing(true)

            setShowModal(true)
          })
      } else {
        toast.error(word("program.session.displayFullScreenNotSupported.title"), {
          description: word("program.session.displayFullScreenNotSupported.content"),
          classNames: errorToastClassName(),
        })
      }
    },
  }))

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)

      //restore owner theme on exit (can occur out of onHide function if navigation changes)
      if (isThemeSwap) setTheme(Theme.OwnerDark)
    }
  }, [])

  return (
    <Dialog
      open={showModal}
      onOpenChange={() => {
        //restore owner theme on exit
        if (isThemeSwap) setTheme(Theme.OwnerDark)

        setShowModal(false)

        setFullscreenToastQueueing(false)

        document.exitFullscreen().catch((err) => {
          if (err instanceof Error) console.error(`Error exiting browser fullscreen: ${err.message}`)
        })
      }}
    >
      <DialogHeader className="sr-only">
        <DialogTitle></DialogTitle>
      </DialogHeader>
      <DialogContent className="h-screen max-w-screen rounded-none p-0" showCloseButton={showIcons}>
        {children}
      </DialogContent>
    </Dialog>
  )
}
